import * as Server from '@/api/server';
import Menu from '@/interfaces/Menu';
import MenuGroup from '@/interfaces/MenuGroup';
import RequestAdLog from '@/interfaces/RequestAdLog';
import ResponseAd from '@/interfaces/ResponseAd';
import { useLocalStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { useLogger } from 'vue-logger-plugin';

export const useAdStore = defineStore('ad', () => {
  let ads: ResponseAd[] = [];
  const adLogs: RequestAdLog[] = [];
  const intervalTime = import.meta.env.VITE_INIT_AD_LOG_TIME || 600000;

  const logger = useLogger();
  const accumulatedAdLogs = useLocalStorage('ADVERTISEMENT_LOG', [] as RequestAdLog[]);

  /**
   * 사용하는 광고만 필터링 및 정렬하여 설정
   * @param responseAds 광고 데이터
   */
  const setAds = (responseAds: ResponseAd[]) => {
    ads = responseAds
      .filter((ad) => !ad.Hide && (ad.DisplayLocation === '2' || ad.DisplayLocation === '3'))
      .sort((a, b) => a.SortNumber - b.SortNumber);
  };

  /**
   * 메뉴 또는 메뉴그룹에 광고 이미지 경로 적용
   * - 광고가 없는 경우에는 아무것도 하지 않음
   * @param menu 메뉴
   * @param menuGroups 메뉴 그룹 리스트
   */
  const applyAdImagePath = (menu: Menu, menuGroups: MenuGroup[]) => {
    // 광고가 없는 경우
    if (!ads?.length) return;

    // 메뉴 이름을 키워드로 광고 조회
    const foundAd = ads.find((ad) => ad.ClassData?.split(',').includes(menu.Name));

    // 메뉴명과 일치하는 광고가 없는 경우
    if (!foundAd) return;

    // 광고 이미지 경로
    const adImagePath = `${foundAd.FilePath}/${foundAd.SaveFileName}`;

    // 메뉴 데이터에 광고 이미지 경로 적용
    if (foundAd.DisplayLocation === '2') {
      // 메뉴 광고인 경우 메뉴 데이터 광고 파일 경로 설정
      menu.ADImagePath = adImagePath;
    } else if (foundAd.DisplayLocation === '3') {
      // 카테고리 광고인 경우 해당 메뉴의 카테고리 데이터를 찾아서 광고 파일 경로 설정
      const foundMenuGroup = menuGroups.find((menuGroup) => menuGroup.GroupCode === menu.GroupCode);
      if (foundMenuGroup) foundMenuGroup.ADImagePath = adImagePath;
    }

    // 현재 조회된 광고가 로그에 없는 경우 로그에 추가
    // 동일한 메뉴가 여러 광고에서 조회된 경우 처음 검색된 광고로 적용
    if (adLogs.every((adLog) => adLog.ADDisplayItemID !== foundAd.ID)) {
      const log: RequestAdLog = {
        ADDisplayItemID: foundAd.ID,
        DisplayTime: 0,
        DisplayStartDateTime: new Date().toISOString(),
        DisplayEndDateTime: null,
      };
      adLogs.push(log);
    }
  };

  /** 광고 로그 동기화 타이머 시작 */
  const startSyncAdLogs = () => {
    setTimeout(syncAdLogs, intervalTime);
  };

  /**
   * 광고 로그 동기화
   * - 광고 로그를 누적 광고 로그에 추가 후 시간 초기화
   * - 누적 광고 로그를 서버에 업로드 후 성공 시 누적 광고 로그 초기화
   * - 업로드 실패 시 누적 광고 로그 유지
   * - 재귀 호출로 주기적으로 광고 로그 동기화
   */
  const syncAdLogs = async () => {
    // 광고 로그가 없고 누적 광고 로그도 없는 경우 실행 안함
    if (!adLogs.length && !accumulatedAdLogs.value.length) return;

    // 업로드 실패로 누적된 데이터가 만건을 넘으면 초기화
    if (accumulatedAdLogs.value.length > 10000) {
      accumulatedAdLogs.value = [];
    }

    // 현재 시간 조회
    const currentDateTime = dayjs();

    // 광고 로그를 누적 광고 로그에 추가 후 시간 초기화
    adLogs.forEach((log) => {
      // 누적 전에 표기 시간 및 종료 시간 설정
      log.DisplayTime = currentDateTime.diff(dayjs(log.DisplayStartDateTime), 's');
      log.DisplayEndDateTime = currentDateTime.toISOString();

      // 누적 로그에 추가
      accumulatedAdLogs.value.push({ ...log }); // 객체 복사해서 push (필요에 따라)

      // 광고 로그 초기화
      log.DisplayTime = 0;
      log.DisplayStartDateTime = currentDateTime.toISOString();
      log.DisplayEndDateTime = null;
    });

    // 누적 광고 로그를 서버에 업로드
    try {
      await Server.uploadAdLogs(accumulatedAdLogs.value);
      // 업로드 성공한 경우 누적 광고 로그 초기화
      accumulatedAdLogs.value = [];
    } catch (error: any) {
      logger.info('[AdStore] 광고 로그 업로드 실패 : ', error);
    }

    // 재귀 호출로 주기적으로 광고 로그 동기화
    setTimeout(syncAdLogs, intervalTime);
  };

  return {
    setAds,
    applyAdImagePath,
    startSyncAdLogs,
  };
});
